import React, { useEffect, useMemo } from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { ArrowSideScrollContainer } from '@lumapps/lumx-layouts/components/ArrowSideScrollContainer';
import { mdiClose } from '@lumapps/lumx/icons';
import {
    Button,
    Dialog,
    DialogProps,
    Emphasis,
    Heading,
    Size,
    Toolbar,
    TabProvider,
    TabList,
    TabPanel,
    Tab,
    Text,
} from '@lumapps/lumx/react';
import { ReactionCounts, ReactionResourceRef, ReactionType } from '@lumapps/reactions-core/api/types';
import { REACTIONS_SCOPE } from '@lumapps/reactions-core/constants';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { UserListProps } from '@lumapps/user-ui/components/UserList';

import { useReactionUserListDialog } from '../../hooks/useReactionUserListDialog';
import { REACTIONS_REACT } from '../../keys';
import { ReactionUserList } from './ReactionUserList';

import './index.scss';

export interface ReactionUserListDialogProps {
    /** Reaction resource ref */
    resourceRef: ReactionResourceRef;
    /** Dialog is open */
    isOpen: boolean;
    /** Callback on close (click away or close button pressed) */
    onClose(): void;
    /** Number passed by parent component that indicates the number of people who reacted */
    reactionCounts: ReactionCounts;
    /** Element to focus on close */
    parentElement?: DialogProps['parentElement'];
    /** Weather or not, we want to show the reaction icon on the user */
    showUserReaction: boolean;
    /**
     * Preload the modal with a specific reaction type.
     * Used by the single reaction feature, to only fetch the likes.
     * @default: undefined a.k.a. all.
     * */
    reactionType?: ReactionType;
}

const CLASSNAME = 'reaction-user-list-dialog';

/**
 * List users that reacted to a resource.
 */
export const ReactionUserListDialog: React.FC<ReactionUserListDialogProps> = ({
    resourceRef,
    isOpen,
    onClose,
    reactionCounts,
    parentElement,
    showUserReaction,
    reactionType,
}) => {
    const { get } = useDataAttributes(REACTIONS_SCOPE);
    const { translateKey } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);
    const [currentReaction, setCurrentReaction] = React.useState<ReactionType | undefined>(reactionType);

    /** Get all non-empty reactions panels to show. */
    const reactionPanels = useMemo(() => reactionCounts.details.filter(({ count }) => count), [reactionCounts.details]);

    const {
        isLoading,
        fetchNextPage,
        hasNextPage,
        items: users,
    } = useReactionUserListDialog(resourceRef, isOpen, currentReaction);

    const title = [reactionCounts.total, translateKey(REACTIONS_REACT.USER_LIST_LIKES)].filter(Boolean).join(' ');

    const userListProps: UserListProps = useMemo(
        () => ({
            isLoading,
            hasMore: hasNextPage,
            onLoadMore: fetchNextPage,
            users: users.map(({ user: { userId, fullName } }) => ({
                id: userId,
                fullName,
            })),
        }),
        [fetchNextPage, hasNextPage, isLoading, users],
    );

    useEffect(() => {
        /** Clear dialog state on open. */
        if (isOpen) {
            setCurrentReaction(undefined);
        }
    }, [isOpen]);

    return (
        <Dialog
            size={Size.regular}
            onClose={onClose}
            isOpen={isOpen}
            forceHeaderDivider
            forceFooterDivider
            parentElement={parentElement}
            className={block()}
        >
            <header>
                <Toolbar label={<Heading typography="title">{title}</Heading>} />
            </header>
            {!reactionType && reactionPanels.length > 1 ? (
                <TabProvider
                    activeTabIndex={
                        currentReaction ? reactionPanels.findIndex(({ type }) => type === currentReaction) + 1 : 0
                    }
                    onChange={(index) => {
                        if (index === 0) {
                            setCurrentReaction(undefined);
                        } else {
                            setCurrentReaction(reactionPanels[index - 1].type);
                        }
                    }}
                >
                    <ArrowSideScrollContainer scrollZoneProps={{ fillSpace: true }}>
                        <TabList className={element('tablist')} aria-label="Filter by reaction">
                            {reactionPanels.length > 1 && (
                                <>
                                    <ArrowSideScrollContainer.Item>
                                        <Tab
                                            role="tab"
                                            label={`${translateKey(GLOBAL.ALL)} ${reactionCounts.total}`}
                                            {...get({
                                                element: 'reaction-user-list-tab',
                                                action: 'select',
                                                type: 'ALL',
                                            })}
                                        />
                                    </ArrowSideScrollContainer.Item>
                                    {reactionPanels.map(({ count, type }) => (
                                        <ArrowSideScrollContainer.Item key={`tab-${type}`}>
                                            <Tab
                                                label={
                                                    <>
                                                        <Text as="span" typography="body1" color="dark">
                                                            {type}
                                                        </Text>{' '}
                                                        {count}
                                                    </>
                                                }
                                                {...get({
                                                    element: 'reaction-user-list-tab',
                                                    action: 'select',
                                                    type,
                                                })}
                                            />
                                        </ArrowSideScrollContainer.Item>
                                    ))}
                                </>
                            )}
                        </TabList>
                    </ArrowSideScrollContainer>

                    <TabPanel key="all">
                        <ReactionUserList
                            showUserReaction={showUserReaction}
                            users={users}
                            userListProps={userListProps}
                        />
                    </TabPanel>

                    {reactionPanels?.map(({ type }) => (
                        <TabPanel key={`tab-panel-${type}`}>
                            <ReactionUserList
                                showUserReaction={showUserReaction}
                                users={users}
                                userListProps={userListProps}
                            />
                        </TabPanel>
                    ))}
                </TabProvider>
            ) : (
                <ReactionUserList showUserReaction={showUserReaction} users={users} userListProps={userListProps} />
            )}

            <footer>
                <Toolbar
                    after={
                        <Button emphasis={Emphasis.medium} icon={mdiClose} onClick={onClose}>
                            {translateKey(GLOBAL.CLOSE)}
                        </Button>
                    }
                />
            </footer>
        </Dialog>
    );
};
ReactionUserListDialog.displayName = 'ReactionUserListDialog';
